import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, FormField, Input, Select } from '@/components/ui';
import type { OrgUnitType } from '@/api/orgAdmin';
import styles from './UnitTypePicker.module.css';

/** Stable id so the label can point at antd's rendered control. */
const SELECT_ID = 'hz-unit-type-select';

export interface UnitTypePickerProps {
    /** Types offered in the list — normally `activeUnitTypes`, plus the current one when hidden. */
    options: OrgUnitType[];
    value: string;
    onChange: (unitTypeId: string) => void;
    /** Super-admin only. When false this is the picker alone, with no create branch. */
    canCreate: boolean;
    /**
     * The PARENT đơn vị's type, when there is one. A type created from here is by
     * construction meant to sit under that parent, so the containment edge is
     * added to it — see `useOrgUnitTypes.createInlineUnitType`.
     */
    parentUnitTypeId: string | null;
    onCreate: (name: string, parentUnitTypeId: string | null) => Promise<OrgUnitType | null>;
    creating: boolean;
    disabled?: boolean;
    /** Rendered under the control; the caller owns validation messaging. */
    error?: string | null;
}

/**
 * EAA-1838: pick a "Loại đơn vị", or create one right here.
 *
 * THIS IS THE FIX FOR "không biết thêm cấp bậc ở đâu". Before this, the ONLY way
 * to author a rung was a page-level panel opened from a toolbar button labelled
 * "Cấp bậc" — which reads like a filter, not an authoring surface — whose own
 * "Thêm cấp bậc" button sat in the card FOOTER, below three paragraphs of notes
 * and a callout, far from the table it appended to. An admin who discovered
 * mid-create that the type they needed did not exist had to abandon the modal,
 * find the panel, and come back. The empty state did not even carry a create
 * button.
 *
 * So the create action now lives exactly where the need is discovered. Everything
 * else about the split is preserved: authoring is still `POST .../unit-types`
 * (super-admin only), assigning is still `PATCH /organizations/{id}`, and an
 * org-admin sees the identical control minus the create branch — a plain select,
 * which is the correct degradation rather than a hidden feature.
 *
 * EAA-1826: the control is the kit's `Select`, not a native `<select>`. The note
 * that used to sit here justified the native element by saying `apps/portal` "is
 * a standalone repo with no Ant Design and no `@ai-content-hub/*` dependency".
 * That has not been true since EAA-1804: `@/components/ui` is a barrel over
 * `@ai-content-hub/ui`, and the portal depends on antd directly. The claim was
 * being quoted forward into new code while the native element rendered the OS
 * menu — square corners, OS highlight, none of the sheet's Dropdown Menu chrome.
 * `Select` keeps the keyboard and screen-reader behaviour the native element was
 * being kept for, and `showSearch` covers the type-ahead the old note wanted.
 *
 * The disclosed inline create form below is unchanged — that is the part this
 * component exists for.
 */
export function UnitTypePicker({
    options,
    value,
    onChange,
    canCreate,
    parentUnitTypeId,
    onCreate,
    creating,
    disabled,
    error,
}: UnitTypePickerProps) {
    const { t } = useTranslation();
    const [adding, setAdding] = useState(false);
    const [draftName, setDraftName] = useState('');
    const [localError, setLocalError] = useState<string | null>(null);

    // "(đã ẩn)" is folded into the label rather than rendered as a sibling node:
    // `optionFilterProp="label"` searches this string, so a deactivated type
    // stays findable by that word.
    const selectOptions = useMemo(
        () =>
            options.map((unitType) => ({
                value: unitType.id,
                label: unitType.is_active
                    ? unitType.name
                    : t('console.hierarchy.unitTypePicker.hiddenSuffixTemplate', {
                          name: unitType.name,
                      }),
            })),
        [options, t]
    );

    async function submitDraft() {
        const name = draftName.trim();
        if (!name) {
            setLocalError(t('console.hierarchy.unitTypePicker.nameRequiredError'));
            return;
        }
        const created = await onCreate(name, parentUnitTypeId);
        if (!created) {
            setLocalError(t('console.hierarchy.unitTypePicker.createFailedError'));
            return;
        }
        // Select what was just created — the whole reason this is inline.
        onChange(created.id);
        setAdding(false);
        setDraftName('');
        setLocalError(null);
    }

    return (
        <div className={styles.picker}>
            {/* `htmlFor`/`id` rather than wrapping: antd's Select renders a div,
                not a form control, so an enclosing <label> would label nothing. */}
            <label className="portal-field-label" htmlFor={SELECT_ID}>
                {t('console.hierarchy.unitTypePicker.label')}
                {/* EAA-1838: required only once a vocabulary EXISTS. No default
                    type is seeded any more, so an org with none is a real and
                    fully permissive state — marking the field required then
                    would demand something unpickable. */}
                {options.length > 0 && (
                    <span className="portal-field-required" aria-hidden="true"> *</span>
                )}
                {options.length === 0 && (
                    <span className="portal-field-optional">
                        {' '}
                        {t('console.hierarchy.unitTypePicker.noneAvailable')}
                    </span>
                )}
            </label>
            {/* EAA-1826: `required` is gone with the native element and nothing is
                lost — `onSubmitCreate` already guards this case itself and says so
                (it could not rely on the attribute, because the inline-create
                branch disables the control while a draft is open). */}
            <Select
                id={SELECT_ID}
                value={value || undefined}
                onChange={(next) => onChange((next as string) ?? '')}
                disabled={disabled || adding || options.length === 0}
                options={selectOptions}
                placeholder={t('console.hierarchy.unitTypePicker.selectPlaceholder')}
                // The vocabulary is admin-authored and unbounded, so type-ahead is
                // the point of moving off the native element. Filter on `label`:
                // the option values are UUIDs, which no one types.
                showSearch
                optionFilterProp="label"
                allowClear
                style={{ width: '100%' }}
            />

            {/* EAA-1804: kit `Button` instead of hand-written `<button className="portal-btn">`.
                `portal-btn-link` had no CSS anywhere (grep confirmed), so this rendered as a
                plain portal-btn already — kept as a plain `<Button>` rather than `type="link"`,
                which would change the look. `UnitTypePicker.module.css`'s `.add` carries
                real layout CSS and is kept via `className`. */}
            {canCreate && !adding && (
                <Button
                    className={styles.add}
                    onClick={() => {
                        setAdding(true);
                        setLocalError(null);
                    }}
                    disabled={disabled}
                >
                    {t('console.hierarchy.unitTypePicker.addNewButton')}
                </Button>
            )}

            {canCreate && adding && (
                // Not a nested <form>: this modal's fields already sit inside the
                // create-org form, and a nested form is invalid HTML — Enter here
                // would submit the OUTER form and create the đơn vị with no type.
                // Hence the explicit keydown handler and type="button" below.
                <div className={styles.draft}>
                    {/* EAA-1804: REAL BUG fix — `submitDraft` sets `localError` on a
                        name-required or create failure WITHOUT closing the draft
                        (`adding` stays true), so this hint and `localError` used to
                        render as two separate unconditional/conditional paragraphs
                        at once — the exact "hint AND error stacked" shape the
                        design never draws. `FormField` enforces hint-or-error by
                        construction, so wiring both through it here removes the
                        stacking outright rather than relying on callers to
                        remember not to double-render. */}
                    <FormField
                        label={t('console.hierarchy.unitTypePicker.newNameLabel')}
                        hint={
                            <>
                                {parentUnitTypeId
                                    ? t('console.hierarchy.unitTypePicker.newUnderParentHint')
                                    : t('console.hierarchy.unitTypePicker.newAtRootHint')}{' '}
                                {t('console.hierarchy.unitTypePicker.autoCodeHint')}
                            </>
                        }
                        error={localError}
                    >
                        <Input
                            placeholder={t('console.hierarchy.unitTypePicker.newNamePlaceholder')}
                            value={draftName}
                            autoFocus
                            disabled={creating}
                            onChange={(e) => setDraftName(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    e.preventDefault();
                                    void submitDraft();
                                }
                                if (e.key === 'Escape') {
                                    e.preventDefault();
                                    setAdding(false);
                                    setLocalError(null);
                                }
                            }}
                        />
                    </FormField>
                    <span className={styles.draftActions}>
                        <Button
                            type="primary"
                            onClick={() => void submitDraft()}
                            disabled={creating}
                        >
                            {creating
                                ? t('console.hierarchy.unitTypePicker.creatingLabel')
                                : t('console.hierarchy.unitTypePicker.createButton')}
                        </Button>
                        <Button
                            onClick={() => {
                                setAdding(false);
                                setLocalError(null);
                            }}
                            disabled={creating}
                        >
                            {t('common.cancel')}
                        </Button>
                    </span>
                </div>
            )}

            {!canCreate && options.length === 0 && (
                <p className="portal-admin-hint">
                    {t('console.hierarchy.unitTypePicker.noneCreatableHint')}
                </p>
            )}

            {/* EAA-1804: `localError` no longer belongs here — it is only ever set
                while the draft is open (see `submitDraft`) and now renders inside
                that draft's own `FormField` above. This paragraph is left for the
                external `error` prop only (unused by either current caller, but
                part of this component's public surface). */}
            {error && <p className="portal-admin-error">{error}</p>}
        </div>
    );
}

export default UnitTypePicker;
